import {
  Card,
  Button,
  InfiniteScrollGrid,
  Avatar
} from "@etframework/ui.components";
import { useNavigate } from "react-router-dom";
import { AppDataManager } from "../../utils/dataManager";
import { useAuth } from "oidc-react";
import { ColumnDirective } from "@syncfusion/ej2-react-grids";
import { formatDate } from "../../utils/helpers";
import { DateFormats } from "../../types/DateFormats";
import {
  useApproveRequest,
  useDenyRequest,
  useApproveManyRequest,
  useDenyManyRequest,
  useApproveAllRequest,
  useDenyAllRequest
} from "../../data/mutations/boardPermissionRequestMutations";
import { useState, useRef, useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";

export const BoardPermissionRequests = () => {
  const { t, i18n } = useTranslation(["ManageBoardPermissionRequests"]);
  const gridRef = useRef(null);
  const { userManager } = useAuth();
  const navigate = useNavigate();
  const { mutate: approveRequest } = useApproveRequest();
  const { mutate: denyRequest } = useDenyRequest();
  const { mutate: approveManyRequest } = useApproveManyRequest();
  const { mutate: denyManyRequest } = useDenyManyRequest();
  const { mutate: approveAllRequest } = useApproveAllRequest();
  const { mutate: denyAllRequest } = useDenyAllRequest();
  const [isEmpty, setIsEmpty] = useState(false);
  const [count, setCount] = useState(0);

  const dataManager = new AppDataManager(
    "boardpermissionrequest/pending",
    userManager.signinSilent.bind(userManager)
  );

  const userFullNameTemplate = (props) => {
    return (
      <div className="d-flex align-items-center">
        <Avatar content={props.User.FullName} />
        <span className={`ms-2`}>{props.User.FullName}</span>
      </div>
    );
  };

  const dateRequestedTemplate = (props) => {
    const formattedDate = formatDate(
      props.CreatedDate,
      DateFormats.ShortDateFormat.momentJsFormat
    );

    return formattedDate;
  };

  const actionsTemplate = (props) => {
    return (
      <div className="et-board-permission-requests__actions">
        <button
          onClick={() => {
            denyRequest(
              {
                id: props.Id
              },
              {
                onSuccess: () => {
                  dataManager.refresh();
                }
              }
            );
          }}
          className="ms-1 et-board-permission-requests__action et-board-permission-requests__action--reject"
        >
          <i className="fa-regular fa-times"></i>
          <span>{t("Actions.Reject")}</span>
        </button>
        <button
          className="et-board-permission-requests__action et-board-permission-requests__action--approve"
          onClick={() => {
            approveRequest(
              {
                id: props.Id
              },
              {
                onSuccess: () => {
                  dataManager.refresh();
                }
              }
            );
          }}
        >
          <i className="fa-regular fa-circle-check"></i>
          <span>{t("Actions.Approve")}</span>
        </button>
      </div>
    );
  };

  const getSelectedRows = () => {
    const rows = gridRef.current.getComponentRef().getSelectedRecords();

    return rows;
  };

  const toolbarItemIds = {
    approveSelected: "approve-selected",
    rejectSelected: "reject-selected"
  };

  const approveSelected = {
    id: toolbarItemIds.approveSelected,
    visible: false,
    enabled: true,
    text: t("Actions.ApproveSelected"),
    prefixIcon: "fa-regular fa-check",
    click: () => {
      const rows = getSelectedRows();

      const ids = rows.map((row) => row.Id);

      approveManyRequest(
        {
          requestIds: ids
        },
        {
          onSuccess: () => {
            dataManager.refresh();
          }
        }
      );
    }
  };

  const rejectSelected = {
    id: toolbarItemIds.rejectSelected,
    visible: false,
    enabled: true,
    text: t("Actions.RejectSelected"),
    prefixIcon: "fa-regular fa-trash",
    click: () => {
      const rows = getSelectedRows();

      const ids = rows.map((row) => row.Id);

      denyManyRequest(
        {
          requestIds: ids
        },
        {
          onSuccess: () => {
            dataManager.refresh();
          }
        }
      );
    }
  };

  const toolbarItems = [approveSelected, rejectSelected];

  const onRowSelected = (_, selectedCount) => {
    toolbarItems.forEach((item) => {
      if (item.id === toolbarItemIds.approveSelected) {
        item.enabled = selectedCount > 0;
      } else if (item.id === toolbarItemIds.rejectSelected) {
        item.enabled = selectedCount > 0;
      }
    });

    let itemsToShow = [];

    itemsToShow = toolbarItems
      .filter((item) => item.enabled)
      .map((item) => item.id);

    gridRef.current.setToolbarItemVisibility(itemsToShow, true, true);
  };

  const onRowDeselected = (_, selectedCount) => {
    if (selectedCount === 0) {
      gridRef.current.setToolbarItemVisibility(["search-input"], true, true);
      return;
    }

    toolbarItems.forEach((item) => {
      if (item.id === toolbarItemIds.approveSelected) {
        item.enabled = selectedCount > 0;
      } else if (item.id === toolbarItemIds.rejectSelected) {
        item.enabled = selectedCount > 0;
      }
    });

    let itemsToShow = [];

    itemsToShow = toolbarItems
      .filter((item) => item.enabled)
      .map((item) => item.id);

    gridRef.current.setToolbarItemVisibility(itemsToShow, true, true);
  };

  const onDataBound = () => {
    const gridData = gridRef.current.getComponentRef().getCurrentViewRecords();
    const totalRecords = gridRef?.current?.getGridRef()?.getTotalRecordsCount();

    setIsEmpty(gridData.length === 0);
    setCount(totalRecords);
  };

  useEffect(() => {
    gridRef.current.getComponentRef().refreshHeader();
  }, [i18n.language]);

  return (
    <div className="et-board-permission-requests">
      <Card className="et-board-permission-requests__card ms-2 my-3 border-0">
        <div className="et-board-permission-requests__card-inner d-flex flex-column justify-content-end">
          <Button
            className="et-board-permission-requests__return btn-tertiary"
            iconCss="fa-regular fa-arrow-left-long"
            text={t("Actions.Return")}
            onClick={() => navigate(-1)}
          />
          <div className="et-board-permission-requests__header d-flex justify-content-between">
            <h2 className="et-board-permission-requests__header-title fs-5">
              <Trans
                i18nKey="ManageBoardPermissionRequests:Title"
                values={{
                  count: count
                }}
              >
                <span className="et-board-permission-requests__header-requests-qty" />
              </Trans>
            </h2>
            <div className="et-board-permission-requests__header-actions d-flex gap-3">
              <Button
                onClick={() => {
                  denyAllRequest(
                    {},
                    {
                      onSuccess: () => {
                        dataManager.refresh();
                      }
                    }
                  );
                }}
                className="et-board-permission-requests__header-action btn-secondary"
                text={t("Actions.RejectAll")}
                disabled={isEmpty}
              />
              <Button
                onClick={() => {
                  approveAllRequest(
                    {},
                    {
                      onSuccess: () => {
                        dataManager.refresh();
                      }
                    }
                  );
                }}
                className="et-board-permission-requests__header-action btn-primary"
                text={t("Actions.ApproveAll")}
                disabled={isEmpty}
              />
            </div>
          </div>
          {isEmpty && (
            <div className="et-board-permission-requests__empty-state">
              <i className="et-board-permission-requests__empty-state-icon fa-regular fa-circle-check"></i>
              <p className="et-board-permission-requests__empty-state-description">
                {t("EmptyState.Title")}
              </p>
            </div>
          )}
          <div
            className={`border rounded mt-3 ${isEmpty ? "visually-hidden" : ""}`}
          >
            <InfiniteScrollGrid
              ref={gridRef}
              showHeader={false}
              dataSource={dataManager}
              toolbarItems={toolbarItems}
              rowSelected={onRowSelected}
              rowDeselected={onRowDeselected}
              dataBound={onDataBound}
              height="calc(100vh - 300px)"
            >
              <ColumnDirective
                field="User.FullName"
                headerText={t("GridHeaders.User")}
                template={userFullNameTemplate}
              />
              <ColumnDirective
                field="Board.Name"
                headerText={t("GridHeaders.Board")}
              />
              <ColumnDirective
                type="date"
                field="CreatedDate"
                headerText={t("GridHeaders.RequestDate")}
                template={dateRequestedTemplate}
              />
              <ColumnDirective template={actionsTemplate} />
            </InfiniteScrollGrid>
          </div>
        </div>
      </Card>
    </div>
  );
};
